/**
 * Portal registry — §3.4.
 *
 * Five role-scoped applications in one codebase. Adding a portal means adding
 * one entry here; the router, the navigation and the role switcher all read
 * from this list, so they cannot drift apart.
 *
 * Every `component` is a `lazy()` import, which is what puts each portal in its
 * own chunk. NFR-18 requires that a learner's initial download contains no
 * instructor, institution, organizer or ops code — the boundary is here.
 */
import { Building2, CalendarDays, GraduationCap, Presentation, ShieldCheck } from 'lucide-react'
import { lazy, type LazyExoticComponent } from 'react'

import type { Role } from '@/shared/auth/roles'

export type PortalId = 'learner' | 'instructor' | 'institution' | 'organizer' | 'ops'

export type PortalDefinition = {
  id: PortalId
  /** Route path, relative to the app root. */
  path: string
  /** Absolute href used by navigation links. */
  href: string
  roles: readonly Role[]
  /** i18n namespace holding this portal's strings. */
  namespace: string
  /** Key in the `common` namespace for the navigation label. */
  labelKey: string
  icon: typeof GraduationCap
  component: LazyExoticComponent<() => React.ReactNode>
  /**
   * Reachable without signing in.
   *
   * True only for the learner surface, which holds the public catalogue from
   * Phase 6. Everything else redirects an anonymous visitor to log in.
   */
  allowAnonymous?: boolean
}

export const PORTALS: readonly PortalDefinition[] = [
  {
    id: 'learner',
    path: '/',
    href: '/',
    roles: ['learner', 'instructor', 'institution_admin', 'moderator', 'platform_admin'],
    namespace: 'learner',
    labelKey: 'portal.learner',
    icon: GraduationCap,
    component: lazy(() => import('@/portals/learner')),
    // The catalogue is public (§3.3). Sending an anonymous visitor to a login
    // form is how a browsable product turns into a walled one.
    allowAnonymous: true,
  },
  {
    id: 'instructor',
    path: '/teach/*',
    href: '/teach',
    roles: ['instructor'],
    namespace: 'instructor',
    labelKey: 'portal.instructor',
    icon: Presentation,
    component: lazy(() => import('@/portals/instructor')),
  },
  {
    id: 'institution',
    path: '/institution/*',
    href: '/institution',
    roles: ['institution_admin'],
    namespace: 'institution',
    labelKey: 'portal.institution',
    icon: Building2,
    component: lazy(() => import('@/portals/institution')),
  },
  {
    id: 'organizer',
    path: '/organize/*',
    href: '/organize',
    // ⚠️ **`platform_admin` since 30 August 2026** — *"the system admin is the
    // only allowed person to create an event"*, then *"remove the organizer
    // role from the system"*. The portal keeps its id, path and namespace; only
    // who may reach it changed. `IsEventOrganizer` is the server-side half and
    // is the actual boundary — this list decides what to *render* (see
    // `shared/auth/roles.ts`).
    roles: ['platform_admin'],
    namespace: 'organizer',
    labelKey: 'portal.organizer',
    icon: CalendarDays,
    component: lazy(() => import('@/portals/organizer')),
  },
  {
    id: 'ops',
    path: '/ops/*',
    href: '/ops',
    roles: ['moderator', 'platform_admin'],
    // English-only at R1 — §12.6.
    namespace: 'common',
    labelKey: 'portal.ops',
    icon: ShieldCheck,
    component: lazy(() => import('@/portals/ops')),
  },
]

export function portalById(id: PortalId): PortalDefinition {
  const portal = PORTALS.find((candidate) => candidate.id === id)
  if (!portal) throw new Error(`Unknown portal: ${id}`)
  return portal
}
